import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api";
import { ErrorNote, Loading, formatBytes } from "../../components/Feedback";
import { Badge, Btn, Card } from "../../components/Shared";
import { useApi } from "../../useApi";
import { AskDialog, SuccessNote, formatWhen } from "./parts";

// Management -> Archived releases (task 2026-10-12-releases-omit-personal-columns-and-survive-a-
// project-delete, O18). A deleted project's releases stay citable: their files moved to the
// release archive (archived-releases/<uuid>/). GET /api/admin/releases lists them; only here an
// admin deletes one (DELETE /api/admin/releases/{uuid}?confirm=<uuid>), files and row, audited.

export default function ArchivedReleasesCard() {
  const r = useApi("/admin/releases");
  const [asking, setAsking] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [deleted, setDeleted] = useState(null);

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.del(`/admin/releases/${asking.uuid}?confirm=${asking.uuid}`);
      setDeleted(asking.title);
      setAsking(null);
      r.reload();
    } catch (err) {
      setError(err);
      setAsking(null);
    } finally {
      setBusy(false);
    }
  };

  if (r.loading && !r.data) return <Loading />;
  if (r.error) return <ErrorNote error={r.error} />;
  const releases = r.data?.releases || [];
  return (
    <Card>
      <h2 style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 700 }}>Archived releases</h2>
      <p style={{ fontSize: 13, color: "#4b5563", margin: "0 0 12px" }}>
        When a project is deleted, its published releases are kept here with their files, so their
        citations and DOIs keep working. The members they name can still open them. Deleting one
        removes its files for good.
      </p>
      <ErrorNote error={error} />
      {deleted && <SuccessNote>Deleted: {deleted}.</SuccessNote>}
      {releases.length === 0 ? (
        <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>No archived release.</p>
      ) : (
        <ul aria-label="Archived releases" style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 10 }}>
          {releases.map(x => (
            <li key={x.uuid} style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 14px" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <Link to={`/releases/${x.uuid}`} style={{ fontWeight: 600 }}>{x.title}</Link>
                {(x.personal_data || []).length > 0 && <Badge color="red">personal data</Badge>}
                {(x.omitted_columns || []).length > 0 && <Badge color="gray">without personal data</Badge>}
                <span style={{ fontSize: 13, color: "#4b5563" }}>
                  {formatWhen(x.finished_at)} · {x.parts.length} files, {formatBytes(x.total_bytes)}
                </span>
              </div>
              {x.citation && <p style={{ margin: "6px 0", fontSize: 13 }}>{x.citation}</p>}
              <Btn variant="danger" onClick={() => setAsking(x)}>Delete release {x.number} of {x.project_slug}</Btn>
            </li>
          ))}
        </ul>
      )}
      {asking && (
        <AskDialog title={`Delete ${asking.title}?`} confirmLabel="Delete release and files" busy={busy}
                   onConfirm={remove} onClose={() => setAsking(null)}>
          <p style={{ fontSize: 14, margin: 0 }}>
            Its files are deleted and cannot be restored. A citation that
            points at it stops resolving.
          </p>
        </AskDialog>
      )}
    </Card>
  );
}
